import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarCheck2,
  Clock3,
  MapPin,
  MessageCircle,
  RefreshCw,
  Video,
  X,
  XCircle,
  Zap,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../../lib/apiClient';
import { usePortalPatientData } from './portalContext';
import { ErrorBanner, PortalPage, statusPillClass } from './ui';

type PortalAppointment = {
  id: string;
  status: string;
  source?: string;
  canCancel?: boolean;
  canReschedule?: boolean;
  title?: string | null;
  startTime: string;
  endTime: string;
  appointmentType?: { id: string; name: string; isTelemed?: boolean } | null;
  provider?: { id: string; name: string; title?: string | null } | null;
  location?: {
    id: string;
    name: string;
    addressLine1?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    timezone?: string | null;
  } | null;
  telemed?: { sessionId?: string | null; joinUrl?: string | null; status?: string | null } | null;
};

type PortalAppointmentsResponse = {
  data: PortalAppointment[];
};

type AvailableProvider = {
  id: string;
  name: string;
  title?: string | null;
  availableTypes: Array<{
    id: string;
    name: string;
    isTelemed?: boolean;
    slotCount: number;
  }>;
  firstAvailableStartTime?: string | null;
};

type SchedulingAvailabilityResponse = {
  data: AvailableProvider[];
};

type ProviderDaySlot = {
  slotKey: string;
  providerId: string;
  date: string;
  startTime: string;
  appointmentTypes: Array<{
    id: string;
    name: string;
    endTime: string;
    durationMinutes: number;
  }>;
};

type ProviderDayResponse = {
  data: ProviderDaySlot[];
};

type PortalWaitlistRow = {
  id: string;
  providerProfileId: string;
  providerName: string;
  providerTitle?: string | null;
  providerType?: string | null;
  date: string;
  queueNumber: number;
  currentRank: number | null;
  status: string;
  patientNotes?: string | null;
  readyByAt?: string | null;
  startTime: string;
  endTime: string;
  cutoffTime: string;
  maxPatients: number;
  createdAt: string;
};

type PortalWaitlistListResponse = {
  data: PortalWaitlistRow[];
};

type WaitlistAvailabilityItem = {
  providerProfileId: string;
  providerUserId?: string | null;
  providerName: string;
  providerTitle?: string | null;
  providerType?: string | null;
  date: string;
  startTime: string;
  endTime: string;
  cutoffTime: string;
  nearTermDays: number;
  maxPatients: number;
  currentCount: number;
  remainingCapacity: number;
  notes?: string | null;
  alreadyJoined?: {
    entryId: string;
    queueNumber: number;
    currentRank: number | null;
    status: string;
    readyByAt?: string | null;
  } | null;
};

type PortalWaitlistAvailabilityResponse = {
  data: WaitlistAvailabilityItem[];
};

type PortalWaitlistOffer = {
  id: string;
  providerName: string;
  providerTitle?: string | null;
  offeredStartTime: string;
  offeredEndTime: string;
  expiresAt: string;
  status: string;
};

type PortalWaitlistOfferResponse = {
  data: PortalWaitlistOffer[];
};

type BookingMode = 'request' | 'instant';

// Maps backend status codes to i18n keys under `appointments.status.*`.
const STATUS_KEYS: Record<string, string> = {
  SCHEDULED: 'scheduled',
  PORTAL_BOOKED: 'portalBooked',
  CHECKED_IN: 'checkedIn',
  COMPLETED: 'completed',
  NO_SHOW: 'noShow',
  CANCELLED: 'cancelled',
};

function parseDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// Date/location formatters take `t` so their fallbacks ("Time TBD",
// "Virtual visit", "Location TBD", ...) are localized alongside the rest of the page.
function formatDateRange(t: TFunction, start?: string | null, end?: string | null) {
  const startDate = parseDate(start);
  if (!startDate) return t('appointments.timeTbd');
  const dateLabel = startDate.toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const startTime = startDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const endDate = parseDate(end);
  const endTime = endDate ? endDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : null;
  return endTime ? `${dateLabel} • ${startTime} - ${endTime}` : `${dateLabel} • ${startTime}`;
}

function formatDateTime(t: TFunction, value?: string | null) {
  const date = parseDate(value);
  return date ? date.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' }) : t('appointments.tbd');
}

function formatDateOnly(t: TFunction, value?: string | null) {
  const date = value ? new Date(`${value}T00:00:00`) : null;
  return date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString([], { dateStyle: 'medium' }) : t('appointments.tbd');
}

function formatLocation(t: TFunction, location?: PortalAppointment['location'] | null, isTelemed?: boolean) {
  if (isTelemed) return t('appointments.virtualVisit');
  if (!location) return t('appointments.locationTbd');
  const parts = [location.addressLine1, location.addressLine2, location.city, location.state, location.postalCode]
    .map((item) => (item ?? '').trim())
    .filter(Boolean);
  const address = parts.length ? parts.join(', ') : null;
  return address ? `${location.name} • ${address}` : location.name;
}

function canCancelAppointment(appointment: PortalAppointment | null) {
  if (!appointment) return false;
  if (appointment.canCancel === false) return false;
  const normalized = appointment.status?.toUpperCase?.() || '';
  if (['CANCELLED', 'PORTAL_BOOKED', 'COMPLETED', 'NO_SHOW', 'CHECKED_IN'].includes(normalized)) return false;
  const startDate = parseDate(appointment.startTime);
  if (!startDate) return false;
  return startDate.getTime() > Date.now();
}

function defaultBookingDate() {
  return new Date().toISOString().slice(0, 10);
}

export default function AppointmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fetchJson } = useApiClient();
  const { activePatientId, buildPortalPath, refreshNonce } = usePortalPatientData();
  // Localized status label resolver (replaces the old module-level normalizeStatus).
  const statusLabel = useCallback(
    (value: string) =>
      STATUS_KEYS[value] ? t(`appointments.status.${STATUS_KEYS[value]}`) : value.replace(/_/g, ' ').toLowerCase(),
    [t],
  );
  const [appointments, setAppointments] = useState<PortalAppointment[]>([]);
  const [waitlists, setWaitlists] = useState<PortalWaitlistRow[]>([]);
  const [waitlistOffers, setWaitlistOffers] = useState<PortalWaitlistOffer[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [offerBusyId, setOfferBusyId] = useState<string | null>(null);
  const [bookingConfirmation, setBookingConfirmation] = useState<string | null>(null);
  const [rescheduleAppointmentId, setRescheduleAppointmentId] = useState<string | null>(null);

  const [bookingMode, setBookingMode] = useState<BookingMode | null>(null);
  const [bookingDate, setBookingDate] = useState(defaultBookingDate);
  const [loadingAvailableProviders, setLoadingAvailableProviders] = useState(false);
  const [loadingProviderDay, setLoadingProviderDay] = useState(false);
  const [availableProviders, setAvailableProviders] = useState<AvailableProvider[]>([]);
  const [selectedBookingProviderId, setSelectedBookingProviderId] = useState('');
  const [providerDaySlots, setProviderDaySlots] = useState<ProviderDaySlot[]>([]);
  const [selectedBookingSlotKey, setSelectedBookingSlotKey] = useState<string | null>(null);
  const [selectedBookingTypeId, setSelectedBookingTypeId] = useState('');
  const [waitlistDialogOpen, setWaitlistDialogOpen] = useState(false);
  const [waitlistDate, setWaitlistDate] = useState(defaultBookingDate);
  const [loadingWaitlistAvailability, setLoadingWaitlistAvailability] = useState(false);
  const [waitlistAvailability, setWaitlistAvailability] = useState<WaitlistAvailabilityItem[]>([]);
  const [waitlistNotes, setWaitlistNotes] = useState<Record<string, string>>({});
  const [waitlistBusyProviderId, setWaitlistBusyProviderId] = useState<string | null>(null);

  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Load each section independently so one failing endpoint can't blank the page.
      const [appointmentResult, waitlistResult, offerResult] = await Promise.allSettled([
        fetchJson<PortalAppointmentsResponse>(buildPortalPath('/portal/appointments')),
        fetchJson<PortalWaitlistListResponse>(buildPortalPath('/portal/waitlists')),
        fetchJson<PortalWaitlistOfferResponse>(buildPortalPath('/portal/waitlist-offers')),
      ]);
      const response = appointmentResult.status === 'fulfilled' ? appointmentResult.value : null;
      const waitlistResponse = waitlistResult.status === 'fulfilled' ? waitlistResult.value : null;
      const offerResponse = offerResult.status === 'fulfilled' ? offerResult.value : null;
      const rows = Array.isArray(response?.data) ? response.data : [];
      const waitlistRows = Array.isArray(waitlistResponse?.data) ? waitlistResponse.data : [];
      const offerRows = Array.isArray(offerResponse?.data) ? offerResponse.data : [];
      setAppointments(rows);
      setWaitlists(waitlistRows);
      setWaitlistOffers(offerRows);
      if (rows.length && !selectedId) {
        setSelectedId(rows[0].id);
      }
      if (appointmentResult.status === 'rejected' && waitlistResult.status === 'rejected' && offerResult.status === 'rejected') {
        setError(appointmentResult.reason?.message || t('appointments.loadError'));
      }
    } catch (err: any) {
      setError(err?.message || t('appointments.loadError'));
    } finally {
      setLoading(false);
    }
  }, [buildPortalPath, fetchJson, selectedId, t]);

  useEffect(() => {
    void loadAppointments();
  }, [loadAppointments, refreshNonce]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const statusValue = statusFilter.toLowerCase();
    return appointments
      .filter((appointment) => {
        const statusMatch = statusValue === 'all' || appointment.status.toLowerCase() === statusValue;
        const name = appointment.title || appointment.appointmentType?.name || 'appointment';
        const provider = appointment.provider?.name || '';
        const telemedLabel = appointment.appointmentType?.isTelemed ? 'telemedicine' : '';
        const textMatch =
          !normalizedQuery ||
          name.toLowerCase().includes(normalizedQuery) ||
          provider.toLowerCase().includes(normalizedQuery) ||
          telemedLabel.includes(normalizedQuery);
        return statusMatch && textMatch;
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
  }, [appointments, query, statusFilter]);

  const selected = useMemo(
    () => appointments.find((item) => item.id === selectedId) || appointments[0] || null,
    [appointments, selectedId],
  );

  const bookingDialogOpen = bookingMode !== null;
  const selectedBookingProvider = useMemo(
    () => availableProviders.find((provider) => provider.id === selectedBookingProviderId) || null,
    [availableProviders, selectedBookingProviderId],
  );
  const selectedBookingSlot = useMemo(
    () => providerDaySlots.find((slot) => slot.slotKey === selectedBookingSlotKey) || null,
    [providerDaySlots, selectedBookingSlotKey],
  );
  const selectedBookingType = useMemo(
    () => selectedBookingSlot?.appointmentTypes.find((type) => type.id === selectedBookingTypeId) || null,
    [selectedBookingSlot, selectedBookingTypeId],
  );

  const closeBookingDialog = useCallback(() => {
    setBookingMode(null);
    setBookingDate(defaultBookingDate());
    setAvailableProviders([]);
    setSelectedBookingProviderId('');
    setProviderDaySlots([]);
    setSelectedBookingSlotKey(null);
    setSelectedBookingTypeId('');
    setRescheduleAppointmentId(null);
  }, []);

  const closeWaitlistDialog = useCallback(() => {
    setWaitlistDialogOpen(false);
    setWaitlistDate(defaultBookingDate());
    setWaitlistAvailability([]);
    setWaitlistNotes({});
    setWaitlistBusyProviderId(null);
  }, []);

  const openWaitlistDialog = useCallback(() => {
    setError(null);
    setBookingConfirmation(null);
    setWaitlistDialogOpen(true);
    setWaitlistDate(defaultBookingDate());
    setWaitlistAvailability([]);
    setWaitlistNotes({});
    setWaitlistBusyProviderId(null);
  }, []);

  const openBookingDialog = useCallback((mode: BookingMode) => {
    setError(null);
    setBookingConfirmation(null);
    setBookingMode(mode);
    setBookingDate(defaultBookingDate());
    setAvailableProviders([]);
    setSelectedBookingProviderId('');
    setProviderDaySlots([]);
    setSelectedBookingSlotKey(null);
    setSelectedBookingTypeId('');
  }, []);

  const beginReschedule = useCallback((appointment: PortalAppointment) => {
    if (!appointment.provider?.id) return;
    const localStart = parseDate(appointment.startTime);
    setRescheduleAppointmentId(appointment.id);
    setBookingMode('instant');
    setBookingDate(localStart ? localStart.toISOString().slice(0, 10) : defaultBookingDate());
    setSelectedBookingProviderId(appointment.provider.id);
    setSelectedBookingSlotKey(null);
    setSelectedBookingTypeId(appointment.appointmentType?.id || '');
    setBookingConfirmation(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const loadAvailableProviders = useCallback(async () => {
    if (!bookingDialogOpen) return;
    try {
      setLoadingAvailableProviders(true);
      setError(null);
      const params = new URLSearchParams({ date: bookingDate });
      const response = await fetchJson<SchedulingAvailabilityResponse>(
        buildPortalPath(`/portal/scheduling/availability?${params.toString()}`),
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      setAvailableProviders(rows);
      setSelectedBookingProviderId((current) =>
        rows.some((provider) => provider.id === current) ? current : rows[0]?.id || '',
      );
    } catch (err: any) {
      setAvailableProviders([]);
      setSelectedBookingProviderId('');
      setProviderDaySlots([]);
      setSelectedBookingSlotKey(null);
      setSelectedBookingTypeId('');
      setError(err?.message || t('appointments.loadProvidersError'));
    } finally {
      setLoadingAvailableProviders(false);
    }
  }, [bookingDate, bookingDialogOpen, buildPortalPath, fetchJson, t]);

  useEffect(() => {
    if (!bookingDialogOpen) return;
    void loadAvailableProviders();
  }, [bookingDialogOpen, loadAvailableProviders]);

  const loadProviderDay = useCallback(async () => {
    if (!bookingDialogOpen || !selectedBookingProviderId) {
      setProviderDaySlots([]);
      setSelectedBookingSlotKey(null);
      setSelectedBookingTypeId('');
      return;
    }
    try {
      setLoadingProviderDay(true);
      setError(null);
      const params = new URLSearchParams({
        providerId: selectedBookingProviderId,
        date: bookingDate,
      });
      const response = await fetchJson<ProviderDayResponse>(
        buildPortalPath(`/portal/scheduling/provider-day?${params.toString()}`),
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      setProviderDaySlots(rows);
      setSelectedBookingSlotKey((current) => (rows.some((slot) => slot.slotKey === current) ? current : rows[0]?.slotKey || null));
    } catch (err: any) {
      setProviderDaySlots([]);
      setSelectedBookingSlotKey(null);
      setSelectedBookingTypeId('');
      setError(err?.message || t('appointments.loadProviderDayError'));
    } finally {
      setLoadingProviderDay(false);
    }
  }, [bookingDate, bookingDialogOpen, buildPortalPath, fetchJson, selectedBookingProviderId, t]);

  useEffect(() => {
    if (!bookingDialogOpen || !selectedBookingProviderId) return;
    void loadProviderDay();
  }, [bookingDialogOpen, selectedBookingProviderId, loadProviderDay]);

  useEffect(() => {
    if (!selectedBookingSlot) {
      setSelectedBookingTypeId('');
      return;
    }
    if (!selectedBookingSlot.appointmentTypes.some((type) => type.id === selectedBookingTypeId)) {
      setSelectedBookingTypeId(selectedBookingSlot.appointmentTypes[0]?.id || '');
    }
  }, [selectedBookingSlot, selectedBookingTypeId]);

  const loadWaitlistAvailability = useCallback(async () => {
    if (!waitlistDialogOpen) return;
    try {
      setLoadingWaitlistAvailability(true);
      setError(null);
      const params = new URLSearchParams({ date: waitlistDate });
      const response = await fetchJson<PortalWaitlistAvailabilityResponse>(
        buildPortalPath(`/portal/waitlists/availability?${params.toString()}`),
      );
      const rows = Array.isArray(response?.data) ? response.data : [];
      setWaitlistAvailability(rows);
      setWaitlistNotes((current) => {
        const next = { ...current };
        rows.forEach((row) => {
          if (!(row.providerProfileId in next)) next[row.providerProfileId] = '';
        });
        return next;
      });
    } catch (err: any) {
      setWaitlistAvailability([]);
      setError(err?.message || t('appointments.loadWaitlistError'));
    } finally {
      setLoadingWaitlistAvailability(false);
    }
  }, [buildPortalPath, fetchJson, waitlistDate, waitlistDialogOpen, t]);

  useEffect(() => {
    if (!waitlistDialogOpen) return;
    void loadWaitlistAvailability();
  }, [loadWaitlistAvailability, waitlistDialogOpen]);

  const handleJoinWaitlist = useCallback(
    async (item: WaitlistAvailabilityItem) => {
      try {
        setWaitlistBusyProviderId(item.providerProfileId);
        setError(null);
        setBookingConfirmation(null);
        await fetchJson('/portal/waitlists/join', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            providerProfileId: item.providerProfileId,
            date: waitlistDate,
            notes: waitlistNotes[item.providerProfileId] || undefined,
            patientId: activePatientId,
          }),
        } as any);
        setBookingConfirmation(
          t('appointments.waitlistJoined', { provider: item.providerName, date: formatDateOnly(t, waitlistDate) }),
        );
        await loadAppointments();
        await loadWaitlistAvailability();
      } catch (err: any) {
        setError(err?.message || t('appointments.waitlistJoinError'));
      } finally {
        setWaitlistBusyProviderId(null);
      }
    },
    [activePatientId, buildPortalPath, fetchJson, loadAppointments, loadWaitlistAvailability, waitlistDate, waitlistNotes, t],
  );

  const handleAcceptWaitlistOffer = useCallback(
    async (offer: PortalWaitlistOffer) => {
      try {
        setOfferBusyId(offer.id);
        setError(null);
        await fetchJson(buildPortalPath(`/portal/waitlist-offers/${offer.id}/accept`), {
          method: 'POST',
        });
        setBookingConfirmation(t('appointments.offerAcceptedConfirm', { date: formatDateTime(t, offer.offeredStartTime) }));
        await loadAppointments();
      } catch (err: any) {
        setError(err?.message || t('appointments.offerAcceptError'));
      } finally {
        setOfferBusyId(null);
      }
    },
    [buildPortalPath, fetchJson, loadAppointments, t],
  );

  const handleCancel = useCallback(
    async (appointment: PortalAppointment) => {
      const confirmMessage = t('appointments.cancelConfirm');
      if (!window.confirm(confirmMessage)) return;
      try {
        setCancellingId(appointment.id);
        setError(null);
        await fetchJson(buildPortalPath(`/portal/appointments/${appointment.id}/cancel`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        } as any);
        await loadAppointments();
      } catch (err: any) {
        setError(err?.message || t('appointments.cancelError'));
      } finally {
        setCancellingId(null);
      }
    },
    [buildPortalPath, fetchJson, loadAppointments, t],
  );

  const handleConfirmBooking = useCallback(async () => {
    if (!bookingMode || !selectedBookingProviderId || !selectedBookingSlot || !selectedBookingType) return;
    try {
      setBookingBusy(true);
      setError(null);
      setBookingConfirmation(null);

      if (rescheduleAppointmentId) {
        await fetchJson(buildPortalPath(`/portal/appointments/${rescheduleAppointmentId}/reschedule`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slotStartTime: selectedBookingSlot.startTime }),
        } as any);
        setBookingConfirmation(t('appointments.rescheduledConfirm', { date: formatDateTime(t, selectedBookingSlot.startTime) }));
      } else {
        const endpoint = bookingMode === 'instant' ? '/portal/appointments/book' : '/portal/appointments/request';
        await fetchJson(buildPortalPath(endpoint), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            providerId: selectedBookingProviderId,
            appointmentTypeId: selectedBookingType.id,
            slotStartTime: selectedBookingSlot.startTime,
            title: selectedBookingType.name,
          }),
        } as any);
        setBookingConfirmation(
          bookingMode === 'instant'
            ? t('appointments.instantConfirmed', { date: formatDateTime(t, selectedBookingSlot.startTime) })
            : t('appointments.requestSentConfirm', { date: formatDateTime(t, selectedBookingSlot.startTime) }),
        );
      }

      await loadAppointments();
      closeBookingDialog();
    } catch (err: any) {
      setError(err?.message || t('appointments.bookingError'));
    } finally {
      setBookingBusy(false);
    }
  }, [
    bookingMode,
    buildPortalPath,
    closeBookingDialog,
    fetchJson,
    loadAppointments,
    rescheduleAppointmentId,
    selectedBookingProviderId,
    selectedBookingSlot,
    selectedBookingType,
    t,
  ]);

  const handleJoinTelemed = useCallback((appointment: PortalAppointment) => {
    const joinUrl = appointment.telemed?.joinUrl;
    if (joinUrl) {
      window.open(joinUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  return (
    <PortalPage
      title={t('appointments.title')}
      description={t('appointments.description')}
      action={
        <button
          className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
          onClick={loadAppointments}
          disabled={loading}
        >
          <RefreshCw size={14} /> {t('common.refresh')}
        </button>
      }
    >
      {error ? <ErrorBanner message={error} /> : null}
      {bookingConfirmation ? (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {bookingConfirmation}
        </div>
      ) : null}

      <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-900">{t('appointments.bookNextTitle')}</p>
            <p className="mt-1 max-w-2xl text-sm text-slate-500">
              {t('appointments.bookNextBody')}
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => openBookingDialog('request')}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              <CalendarCheck2 size={16} /> {t('appointments.bookAppointment')}
            </button>
            <button
              type="button"
              onClick={openWaitlistDialog}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-lime-400 px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm hover:bg-lime-300"
            >
              <Zap size={16} /> {t('appointments.joinWaitlist')}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
            <input
              className="min-w-[220px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
              placeholder={t('appointments.searchPlaceholder')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <select
              className="rounded-lg border border-slate-200 px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">{t('appointments.status.all')}</option>
              <option value="scheduled">{t('appointments.status.scheduled')}</option>
              <option value="portal_booked">{t('appointments.status.portalBooked')}</option>
              <option value="checked_in">{t('appointments.status.checkedIn')}</option>
              <option value="completed">{t('appointments.status.completed')}</option>
              <option value="cancelled">{t('appointments.status.cancelled')}</option>
              <option value="no_show">{t('appointments.status.noShow')}</option>
            </select>
          </div>

          <div className="rounded-xl border border-slate-100 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-800">{t('appointments.upcomingRecent')}</p>
                <p className="text-xs text-slate-500">{t('appointments.selectToSeeDetails')}</p>
              </div>
              <span className="text-xs text-slate-500">{filtered.length} {t('common.total')}</span>
            </div>
            {loading && <div className="px-4 py-6 text-sm text-slate-500">{t('appointments.loading')}</div>}
            {!loading && filtered.length === 0 && (
              <div className="px-4 py-8 text-sm text-slate-500">{t('appointments.none')}</div>
            )}
            <div className="divide-y divide-slate-100">
              {filtered.map((appointment) => {
                const isActive = appointment.id === selected?.id;
                const isTelemed = Boolean(appointment.appointmentType?.isTelemed);
                const label = appointment.title || appointment.appointmentType?.name || t('common.appointment');
                return (
                  <button
                    key={appointment.id}
                    type="button"
                    onClick={() => setSelectedId(appointment.id)}
                    className={`w-full px-4 py-4 text-left transition hover:bg-slate-50 ${isActive ? 'bg-slate-50' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-slate-800">{label}</p>
                        <p className="mt-1 text-xs text-slate-500">{formatDateRange(t, appointment.startTime, appointment.endTime)}</p>
                        <p className="mt-1 text-xs text-slate-500">{appointment.provider?.name || t('common.careTeam')}</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {isTelemed && <span className="pill pill-info">{t('common.telemedicine')}</span>}
                          {appointment.location?.name && !isTelemed && (
                            <span className="pill pill-info">{appointment.location.name}</span>
                          )}
                          {appointment.source ? (
                            <span className="pill pill-info">{appointment.source === 'PORTAL' ? t('appointments.bookedInPortal') : t('appointments.bookedByStaff')}</span>
                          ) : null}
                        </div>
                      </div>
                      <span className={`pill ${statusPillClass(appointment.status)}`}>{statusLabel(appointment.status)}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            {selected ? (
              <div className="space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">{t('appointments.detailEyebrow')}</p>
                  <h2 className="text-lg font-semibold text-slate-900">
                    {selected.title || selected.appointmentType?.name || t('common.appointment')}
                  </h2>
                </div>
                <div className="space-y-3 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <CalendarCheck2 size={16} className="text-slate-500" />
                    <span>{formatDateTime(t, selected.startTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock3 size={16} className="text-slate-500" />
                    <span>{formatDateRange(t, selected.startTime, selected.endTime)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={16} className="text-slate-500" />
                    <span>{formatLocation(t, selected.location, selected.appointmentType?.isTelemed)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`pill ${statusPillClass(selected.status)}`}>{statusLabel(selected.status)}</span>
                    {selected.appointmentType?.isTelemed && <span className="pill pill-info">{t('common.virtualVisit')}</span>}
                    {selected.source ? <span className="pill pill-info">{selected.source === 'PORTAL' ? t('appointments.portalLabel') : t('appointments.staffLabel')}</span> : null}
                  </div>
                  <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2">
                    <p className="text-xs text-slate-500">{t('appointments.provider')}</p>
                    <p className="font-medium text-slate-800">{selected.provider?.name || t('common.careTeam')}</p>
                    {selected.provider?.title && <p className="text-xs text-slate-500">{selected.provider.title}</p>}
                  </div>
                  {selected.status === 'PORTAL_BOOKED' && (
                    <div className="rounded-lg border border-lime-200 bg-lime-50 px-3 py-2 text-xs text-lime-900">
                      {t('appointments.portalBookedNote')}
                    </div>
                  )}
                  {selected.appointmentType?.isTelemed && (
                    <div className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                      <p className="font-semibold text-slate-700">{t('appointments.telemedReadiness')}</p>
                      <p className="mt-1">
                        {selected.telemed?.joinUrl ? t('appointments.joinLinkReady') : t('appointments.joinLinkPending')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-sm text-slate-500">{t('appointments.selectToView')}</div>
            )}
          </div>

          <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">{t('appointments.waitlistsEyebrow')}</p>
                <p className="mt-1 text-sm text-slate-500">{t('appointments.waitlistsSub')}</p>
              </div>
              <span className="text-xs text-slate-500">{waitlists.length} {t('common.active')}</span>
            </div>
            {waitlistOffers.length ? (
              <div className="mt-4 space-y-3">
                {waitlistOffers.map((offer) => (
                  <div key={offer.id} className="rounded-xl border border-lime-200 bg-lime-50 px-3 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-semibold text-slate-900">{t('appointments.earlierSlot')}</p>
                        <p className="text-xs text-slate-600">
                          {offer.providerName} • {formatDateRange(t, offer.offeredStartTime, offer.offeredEndTime)}
                        </p>
                        <p className="mt-1 text-[11px] text-lime-800">{t('appointments.expires', { value: formatDateTime(t, offer.expiresAt) })}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => void handleAcceptWaitlistOffer(offer)}
                        disabled={offerBusyId === offer.id}
                        className="rounded-lg bg-lime-500 px-3 py-2 text-xs font-semibold text-white disabled:opacity-60"
                      >
                        {offerBusyId === offer.id ? t('appointments.accepting') : t('appointments.accept')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
            {!waitlists.length ? (
              <div className="mt-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-3 py-4 text-sm text-slate-500">
                {t('appointments.noWaitlists')}
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {waitlists.map((item) => (
                  <div key={item.id} className="rounded-lg border border-slate-100 bg-slate-50 px-3 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="font-medium text-slate-800">{item.providerName}</p>
                      <span className={`pill ${item.status === 'SEE_DOCTOR_SOON' ? 'pill-success' : 'pill-info'}`}>
                        {item.status === 'SEE_DOCTOR_SOON' ? t('appointments.status.seeDoctorSoon') : t('appointments.status.waiting')}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500">
                      {formatDateOnly(t, item.date)} • {t('appointments.queue', { number: item.queueNumber })}
                      {item.currentRank ? ` • ${t('appointments.rank', { rank: item.currentRank })}` : ''}
                    </p>
                    {item.readyByAt ? (
                      <p className="mt-2 text-xs font-medium text-emerald-700">{t('appointments.beReadyBy', { value: formatDateTime(t, item.readyByAt) })}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-3 rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-400">{t('appointments.actions')}</p>
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => selected && handleJoinTelemed(selected)}
              disabled={!selected?.appointmentType?.isTelemed || !selected?.telemed?.joinUrl}
            >
              <Video size={16} /> {t('appointments.joinVirtual')}
            </button>
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => navigate('/messages')}
              disabled={!selected}
            >
              <MessageCircle size={16} /> {t('appointments.messageCareTeam')}
            </button>
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => selected && beginReschedule(selected)}
              disabled={!selected?.canReschedule}
            >
              <RefreshCw size={16} /> {t('appointments.reschedule')}
            </button>
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-rose-200 px-3 py-2 text-sm text-rose-700 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={() => selected && handleCancel(selected)}
              disabled={!canCancelAppointment(selected) || cancellingId === selected?.id}
            >
              <XCircle size={16} />
              {cancellingId === selected?.id ? t('appointments.cancelling') : t('appointments.cancel')}
            </button>
          </div>
        </div>
      </div>

      {bookingDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {bookingMode === 'instant' ? t('appointments.booking.instantBadge') : t('appointments.booking.requestBadge')}
                </div>
                <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                  {rescheduleAppointmentId
                    ? t('appointments.booking.rescheduleTitle')
                    : bookingMode === 'instant'
                    ? t('appointments.booking.instantTitle')
                    : t('appointments.booking.requestTitle')}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {bookingMode === 'instant'
                    ? t('appointments.booking.instantSubtitle')
                    : t('appointments.booking.requestSubtitle')}
                </p>
              </div>
              <button
                type="button"
                onClick={closeBookingDialog}
                className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-6 px-6 py-6 lg:grid-cols-[1.2fr_1fr]">
              <div className="space-y-5">
                <section className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('appointments.booking.step1')}</p>
                  <div className="mt-3 flex items-center gap-3">
                    <input
                      type="date"
                      className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                      value={bookingDate}
                      onChange={(event) => setBookingDate(event.target.value)}
                    />
                    <span className="text-xs text-slate-500">{t('appointments.booking.step1Hint')}</span>
                  </div>
                </section>

                <section className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('appointments.booking.step2')}</p>
                  {loadingAvailableProviders ? (
                    <div className="mt-3 text-sm text-slate-500">{t('appointments.booking.loadingDoctors')}</div>
                  ) : availableProviders.length === 0 ? (
                    <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                      {t('appointments.booking.noDoctors')}
                    </div>
                  ) : (
                    <div className="mt-3 grid gap-3">
                      {availableProviders.map((provider) => {
                        const active = provider.id === selectedBookingProviderId;
                        return (
                          <button
                            key={provider.id}
                            type="button"
                            onClick={() => setSelectedBookingProviderId(provider.id)}
                            className={`rounded-2xl border px-4 py-3 text-left transition ${
                              active
                                ? 'border-slate-900 bg-slate-900 text-white'
                                : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div>
                                <p className="font-semibold">{provider.name}</p>
                                {provider.title ? <p className={`text-xs ${active ? 'text-slate-200' : 'text-slate-500'}`}>{provider.title}</p> : null}
                              </div>
                              {provider.firstAvailableStartTime ? (
                                <span className={`text-xs ${active ? 'text-slate-200' : 'text-slate-500'}`}>
                                  {formatDateTime(t, provider.firstAvailableStartTime)}
                                </span>
                              ) : null}
                            </div>
                            <div className="mt-3 flex flex-wrap gap-2">
                              {provider.availableTypes.map((type) => (
                                <span
                                  key={type.id}
                                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                                    active ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-600'
                                  }`}
                                >
                                  {type.name} • {t('appointments.slots', { count: type.slotCount })}
                                </span>
                              ))}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </section>

                <section className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('appointments.booking.step3')}</p>
                  {loadingProviderDay ? (
                    <div className="mt-3 text-sm text-slate-500">{t('appointments.booking.loadingTimes')}</div>
                  ) : !selectedBookingProviderId ? (
                    <div className="mt-3 text-sm text-slate-500">{t('appointments.booking.chooseDoctorFirst')}</div>
                  ) : providerDaySlots.length === 0 ? (
                    <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-white px-4 py-6 text-sm text-slate-500">
                      {t('appointments.booking.noTimes')}
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {providerDaySlots.map((slot) => (
                        <button
                          key={slot.slotKey}
                          type="button"
                          onClick={() => setSelectedBookingSlotKey(slot.slotKey)}
                          className={`rounded-full border px-3 py-2 text-sm transition ${
                            selectedBookingSlotKey === slot.slotKey
                              ? 'border-slate-900 bg-slate-900 text-white'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {formatDateTime(t, slot.startTime)}
                        </button>
                      ))}
                    </div>
                  )}
                </section>

                <section className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('appointments.booking.step4')}</p>
                  {!selectedBookingSlot ? (
                    <div className="mt-3 text-sm text-slate-500">{t('appointments.booking.chooseTimeFirst')}</div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {selectedBookingSlot.appointmentTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setSelectedBookingTypeId(type.id)}
                          className={`rounded-xl border px-3 py-2 text-sm transition ${
                            selectedBookingTypeId === type.id
                              ? bookingMode === 'instant'
                                ? 'border-lime-500 bg-lime-400 text-slate-900'
                                : 'border-slate-900 bg-slate-900 text-white'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {type.name} • {t('appointments.durationMin', { count: type.durationMinutes })}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              <div className="space-y-4">
                <div className="rounded-3xl border border-slate-100 bg-gradient-to-br from-slate-50 to-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('appointments.booking.step5')}</p>
                  <div className="mt-4 space-y-4">
                    <div>
                      <p className="text-xs text-slate-500">{t('appointments.booking.doctor')}</p>
                      <p className="font-semibold text-slate-900">{selectedBookingProvider?.name || t('appointments.booking.selectDoctor')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('appointments.booking.time')}</p>
                      <p className="font-semibold text-slate-900">
                        {selectedBookingSlot ? formatDateTime(t, selectedBookingSlot.startTime) : t('appointments.booking.selectTime')}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('appointments.booking.appointmentType')}</p>
                      <p className="font-semibold text-slate-900">{selectedBookingType?.name || t('appointments.booking.selectAppointmentType')}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">{t('appointments.booking.visitEnd')}</p>
                      <p className="font-semibold text-slate-900">{selectedBookingType ? formatDateTime(t, selectedBookingType.endTime) : t('appointments.tbd')}</p>
                    </div>
                  </div>

                  <div className="mt-5 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-sm text-slate-600">
                    {rescheduleAppointmentId
                      ? t('appointments.booking.rescheduleNote')
                      : bookingMode === 'instant'
                      ? t('appointments.booking.instantNote')
                      : t('appointments.booking.requestNote')}
                  </div>

                  <button
                    type="button"
                    onClick={handleConfirmBooking}
                    disabled={!selectedBookingProviderId || !selectedBookingSlot || !selectedBookingType || bookingBusy}
                    className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-2xl px-4 py-3 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 ${
                      bookingMode === 'instant'
                        ? 'bg-lime-400 text-slate-900 hover:bg-lime-300'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {bookingBusy
                      ? t('appointments.booking.confirming')
                      : rescheduleAppointmentId
                      ? t('appointments.booking.confirmReschedule')
                      : bookingMode === 'instant'
                      ? t('appointments.booking.confirmInstant')
                      : t('appointments.booking.sendRequest')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {waitlistDialogOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-lime-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-700">
                  {t('appointments.waitlistDialog.badge')}
                </div>
                <h2 className="mt-3 text-2xl font-semibold text-slate-900">{t('appointments.waitlistDialog.title')}</h2>
                <p className="mt-1 text-sm text-slate-500">
                  {t('appointments.waitlistDialog.subtitle')}
                </p>
              </div>
              <button
                type="button"
                onClick={closeWaitlistDialog}
                className="rounded-full border border-slate-200 p-2 text-slate-500 hover:bg-slate-50"
              >
                <X size={18} />
              </button>
            </div>

            <div className="max-h-[calc(92vh-96px)] overflow-auto px-6 py-6">
              <section className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{t('appointments.waitlistDialog.selectDay')}</p>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <input
                    type="date"
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
                    value={waitlistDate}
                    onChange={(event) => setWaitlistDate(event.target.value)}
                  />
                  <span className="text-xs text-slate-500">{t('appointments.waitlistDialog.selectDayHint')}</span>
                </div>
              </section>

              <section className="mt-6 space-y-4">
                {loadingWaitlistAvailability ? (
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-6 text-sm text-slate-500">{t('appointments.waitlistDialog.loadingDoctors')}</div>
                ) : waitlistAvailability.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
                    {t('appointments.waitlistDialog.noDoctors')}
                  </div>
                ) : (
                  waitlistAvailability.map((item) => {
                    const alreadyJoined = item.alreadyJoined;
                    const busy = waitlistBusyProviderId === item.providerProfileId;
                    return (
                      <article key={item.providerProfileId} className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                          <div className="space-y-3">
                            <div>
                              <p className="text-lg font-semibold text-slate-900">{item.providerName}</p>
                              {item.providerTitle ? <p className="text-sm text-slate-500">{item.providerTitle}</p> : null}
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs">
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                                {formatDateOnly(t, item.date)} • {item.startTime} - {item.endTime}
                              </span>
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">{t('appointments.waitlistDialog.cutoff', { value: item.cutoffTime })}</span>
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">
                                {t('appointments.waitlistDialog.joinedCount', { count: item.currentCount, max: item.maxPatients })}
                              </span>
                            </div>
                            {item.notes ? (
                              <div className="rounded-2xl border border-slate-100 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                                {item.notes}
                              </div>
                            ) : null}
                            {alreadyJoined ? (
                              <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-sm text-emerald-800">
                                {t('appointments.waitlistDialog.alreadyJoinedTitle')}
                                <div className="mt-1">
                                  {t('appointments.waitlistDialog.alreadyJoinedDetail', { number: alreadyJoined.queueNumber })}
                                  {alreadyJoined.currentRank ? t('appointments.waitlistDialog.rankSuffix', { rank: alreadyJoined.currentRank }) : ''}
                                  {alreadyJoined.readyByAt ? t('appointments.waitlistDialog.readyBySuffix', { value: formatDateTime(t, alreadyJoined.readyByAt) }) : ''}
                                </div>
                              </div>
                            ) : null}
                          </div>

                          <div className="w-full max-w-sm space-y-3">
                            <textarea
                              rows={3}
                              value={waitlistNotes[item.providerProfileId] || ''}
                              onChange={(event) =>
                                setWaitlistNotes((current) => ({
                                  ...current,
                                  [item.providerProfileId]: event.target.value,
                                }))
                              }
                              placeholder={t('appointments.waitlistDialog.notePlaceholder')}
                              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm outline-none focus:ring-2 focus:ring-lime-100"
                              disabled={Boolean(alreadyJoined)}
                            />
                            <button
                              type="button"
                              onClick={() => void handleJoinWaitlist(item)}
                              disabled={Boolean(alreadyJoined) || item.remainingCapacity <= 0 || busy}
                              className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-lime-400 px-4 py-3 text-sm font-semibold text-slate-900 shadow-sm hover:bg-lime-300 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                              {busy ? <RefreshCw size={16} className="animate-spin" /> : <Zap size={16} />}
                              {alreadyJoined ? t('appointments.waitlistDialog.alreadyJoined') : item.remainingCapacity <= 0 ? t('appointments.waitlistDialog.waitlistFull') : t('appointments.waitlistDialog.join')}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })
                )}
              </section>
            </div>
          </div>
        </div>
      ) : null}
    </PortalPage>
  );
}
